import type { DocStreamEvent, DocStreamPhase } from '../types/documentCapture.types';

/**
 * Pure reducers for the live journey stream (86d3uf18j). Extracted from the Zustand store so the
 * tricky reconciliation logic — matching SSE events to optimistic rows by correlation id, guarding
 * against phase downgrades, deriving the session tally — is testable in isolation, without a browser
 * or a running server. The store actions are thin wrappers that call these and do the side effects.
 *
 * The core invariant these enforce: an SSE event finds its row by clientUploadId FIRST (the only id
 * that exists before the upload response), so a lost/slow response can never orphan a phantom row.
 */

/** Newest-first cap for the live journey. Matches the server's replay window. */
export const STREAM_CAP = 50;

/** One file's journey, morphed IN PLACE — never a row per pipeline step. */
export interface JourneyRow {
  /** Stable identity: the correlation id for local uploads, else file id / doc number. */
  key: string;
  fileId: number | null;
  docNumber: string | null;
  prefix: string | null;
  phase: DocStreamPhase;
  /** Original file name, known only for rows this browser uploaded. */
  fileName?: string;
  at: string;
  /** For duplicates: the existing document's state, so the row never says a bare "duplicate". */
  duplicateStatus?: { docStatus: number | null; linkStatus: number | null };
  /**
   * 86d3x48q7 (T13) — this row was identified by a PERSON in the review queue, not by the readers.
   * Flavours the copy ("you confirmed it") so the feed tells the truth about what happened; the feed
   * is where operators learn the pipeline's shape, and a row that silently appeared as `linked`
   * would teach them the machine did it.
   */
  resolvedByYou?: boolean;
  /**
   * Client correlation id (86d3uf18j) minted before the upload and echoed by the server in stream
   * events. It's how an SSE event finds THIS row even when the upload response was lost — so a
   * server success can never orphan a phantom "Failed" row next to the real captured/linked one.
   */
  clientUploadId?: string;
  /**
   * 86d3yeh4j — which try the second reading is on, out of how many are configured. Set by a
   * `retrying` event and CLEARED by any other phase: a row that has moved on must not keep printing
   * "(2 of 4)", which would describe a wait that already ended.
   */
  attempt?: number;
  of?: number;
  /** 86d3ygnz3 — why a `held` row is held. Cleared with the rest when the row moves on. */
  holdReason?: 'outage' | 'quota';
  /**
   * 86d3yeh5j — epoch ms at which the sweep first SAW this row in a finished-and-needs-nobody phase.
   * Not the moment the phase happened: it is stamped by the sweep, so "unstamped" means precisely
   * "nobody was watching this list while it finished" — which is what {@link retireUnwatched} keys
   * the hydrate replay off.
   */
  doneAt?: number;
  /**
   * 86d3yeh5j — this row has stepped aside into the "finished and filed" footer. A DISPLAY decision
   * only: the row stays in the array, so {@link tallyFromRows} still counts it and the footer can put
   * it back. Never set on a row that needs a person — see {@link RETIRABLE_PHASES}.
   */
  retired?: boolean;
}

export interface SessionTally {
  captured: number;
  linked: number;
  duplicates: number;
  failed: number;
}

/**
 * How far along a phase is — used to keep the more-advanced phase when merging rows and to stop a
 * late client callback ('duplicate'/'failed') from downgrading a row the stream already advanced.
 * A deliberate edit ('updated') and a link both outrank a capture; client-terminal phases sit at 3.
 */
export const PHASE_RANK: Record<DocStreamPhase, number> = {
  uploading: 0,
  reading: 1,
  // 86d3x48q7 (T13) — the AI second reading sits between decode and the link wait, because that is
  // where it happens: a scan is read, then read again independently, and only then waits to link.
  //
  // ⚠️ RENUMBERING BELOW THIS LINE IS LOAD-BEARING, not cosmetic. The merge guard keeps the HIGHER
  // rank, so if `verifying` were given a rank at or above the phase that follows it, a late-arriving
  // `verifying` event would outrank an already-`captured` row and silently DOWNGRADE it — the row
  // would sit at "checking" forever while the document was long since linked. Every phase after
  // `reading` shifted +1 to open slot 2.
  verifying: 2,
  // 86d3yeh4j — a retry and a hold are the SAME STEP as `verifying`, seen while it is stalled, so
  // they share its rank rather than taking new slots. Sharing it is what lets either replace a
  // `verifying` row IN PLACE (the row morphs rather than duplicating) and what lets `held` and
  // `retrying` alternate freely, since neither outranks the other.
  //
  // ⚠️ A RANK IS DATA, NOT A GUARD. This comment used to claim that ranking these below the terminal
  // phases was itself enough to stop a late `held` dragging a finished row back to "waiting". It was
  // not: three call sites read this table and each decides for itself what to do with it, and for a
  // long time the SERVER path consulted it not at all. What actually reads it, and how:
  //   • resolveLocalRow   — keep-higher-rank, on the client-callback path (which never sees these two)
  //   • reduceStreamEvent — keep-higher-rank for {@link STALLED_STEP_PHASES} ONLY, and nothing else
  //   • settleResolvedRow — bypasses ranking altogether, on purpose (see its docstring)
  // The renumbering warning above still stands, for all three; the table just isn't a guard by itself.
  retrying: 2,
  held: 2,
  waiting: 3,
  duplicate: 4,
  review: 4,
  failed: 4,
  captured: 5,
  updated: 6,
  linked: 7,
};

/**
 * Derive the tally from the CURRENT journey rows — never accumulate per event. A journey feed is a
 * progress stream (one file emits reading→captured→linked…), so summing per event double-counts and
 * re-increments on hydrate replay. Deriving from the visible rows counts each file once and is
 * idempotent. A `linked` row was also captured, so it counts toward both.
 *
 * 🚫 **This is a summary of the FEED, and nothing else. Never wire it to a surface that says
 * "today".** (86d3yek7d — it was, and the owner caught it.) The rows are the last ≤50 EVENTS the API
 * happens to be holding in memory: ~10-20 files, cleared by every restart, filtered per user, and
 * shrinking as older rows fall out of the window — so this number legitimately changes on every
 * reload and differs between two people looking at the same screen. Day totals come from
 * `GET document-capture/stats/intake-today`, counted in the database.
 *
 * Kept, and kept tested, because the derivation itself is correct and non-obvious: it is the one
 * honest way to describe what is currently on screen.
 */
export function tallyFromRows(rows: JourneyRow[]): SessionTally {
  const t: SessionTally = { captured: 0, linked: 0, duplicates: 0, failed: 0 };
  for (const r of rows) {
    switch (r.phase) {
      case 'linked':
        t.linked += 1;
        t.captured += 1;
        break;
      case 'captured':
        t.captured += 1;
        break;
      case 'duplicate':
        t.duplicates += 1;
        break;
      case 'failed':
        t.failed += 1;
        break;
      default:
        break;
    }
  }
  return t;
}

// ---------------------------------------------------------------- feed retirement (86d3yeh5j)

/**
 * How long a finished row stays on screen before it steps aside — long enough for whoever fed the
 * scanner to look up and see it land. (The design prototype uses 8s so the behaviour is watchable in
 * a demo; 90s is the product number.)
 */
export const RETIRE_HOLD_MS = 90_000;

/** How often the sweep runs. Cheap: it returns the same array when nothing changed, and the store bails. */
export const RETIRE_SWEEP_MS = 1_500;

/**
 * Finished, and needs nobody. These earn their moment on screen and then step aside.
 *
 * ⚠️ An ALLOW-LIST, deliberately — never a deny-list of "phases that need a person". The phase union
 * grows (86d3yeh4j added `held` and `retrying` in the same week this shipped), and under a deny-list
 * every new phase would default to RETIRABLE: a new in-flight state would start hiding itself after
 * 90s, and a new needs-a-person state would hide inside a footer labelled "finished and filed". Under
 * an allow-list a new phase defaults to staying on screen, which is the safe way to be wrong.
 *
 * `waiting` is absent on purpose: it is not finished, and a `waiting` that has gone stale is exactly
 * the row that needs a person to notice it.
 */
export const RETIRABLE_PHASES: ReadonlySet<DocStreamPhase> = new Set<DocStreamPhase>([
  'linked',
  'captured',
  'updated',
  'duplicate',
]);

/**
 * Stamp a finished row, then retire it once it has had its moment. Two passes on purpose: the stamp
 * records when this browser first SAW the row finished, which is what gives every row the same 90s
 * regardless of when the server produced it.
 *
 * Returns the SAME array reference when nothing changed — load-bearing, not an optimisation. The page
 * subscribes to the whole stream and renders the capture surface below it, so a `set` on every tick
 * would re-render all of it every 1.5s forever (CLAUDE.md re-render gate). The store action compares
 * references and bails without calling `set`.
 *
 * `now` is a parameter rather than a `Date.now()` call so the tests need no fake timers.
 */
export function sweepRetired(
  rows: JourneyRow[],
  now: number,
  holdMs: number = RETIRE_HOLD_MS
): JourneyRow[] {
  let changed = false;
  const next = rows.map((row) => {
    if (row.retired || !RETIRABLE_PHASES.has(row.phase)) return row;
    if (row.doneAt == null) {
      changed = true;
      return { ...row, doneAt: now };
    }
    if (now - row.doneAt >= holdMs) {
      changed = true;
      return { ...row, retired: true };
    }
    return row;
  });
  return changed ? next : rows;
}

/**
 * The hydrate post-pass: retire finished rows NOBODY WATCHED.
 *
 * `doneAt == null` is the entire test, and it reads correctly in both cases without a replay flag
 * threaded through the reducers. On a cold load nothing has been stamped, so this morning's finished
 * scans — the clutter this ticket is about — retire the instant they are replayed. On a remount the
 * rows the user watched land are already stamped, so they keep whatever grace is left.
 *
 * ⚠️ Narrow accepted race: a LIVE event that lands between the replay's fetch and this pass is also
 * unstamped, so a scan that finishes in that window (well under one round trip) retires immediately
 * instead of showing for 90s. It is still one click away in the footer.
 */
export function retireUnwatched(rows: JourneyRow[], now: number): JourneyRow[] {
  let changed = false;
  const next = rows.map((row) => {
    if (row.retired || row.doneAt != null || !RETIRABLE_PHASES.has(row.phase)) return row;
    changed = true;
    return { ...row, doneAt: now, retired: true };
  });
  return changed ? next : rows;
}

/**
 * 86d3yeh5j — the retirement fields a row should carry after being morphed to `nextPhase`. Every
 * mutation below spreads this, so the invariant holds in ONE place: **a row that changes phase is
 * never left retired, and always gets a fresh hold.**
 *
 * Both halves matter. Un-retiring is what makes the lagging `linked` visible again (and stops a
 * `review` hiding under "finished and filed"). Clearing `doneAt` is what gives the resurrected row a
 * FULL 90s — keeping the old stamp would let the very next sweep re-retire it, so the confirmation
 * would flicker rather than land.
 *
 * Guarded on CHANGE, not on every event: a repeated same-phase event (a duplicate publish, the
 * hydrate replay re-applying an event this row already saw) must not resurrect a row for nothing.
 */
function carryRetirement(
  row: JourneyRow,
  nextPhase: DocStreamPhase
): Pick<JourneyRow, 'retired' | 'doneAt'> {
  if (nextPhase === row.phase) return { retired: row.retired, doneAt: row.doneAt };
  return { retired: undefined, doneAt: undefined };
}

/**
 * The "the second reading has stalled" phases — the only ones {@link reduceStreamEvent} will refuse.
 *
 * They describe a STEP, not an outcome: `held` (the reader is down for everyone, or its daily budget
 * is spent) and `retrying` (a transient failure, try N of M) are `verifying` seen while it is stuck,
 * which is why they share its rank. That is what makes them safe to refuse and nothing else is: a row
 * that has already reached a further phase has DEMONSTRABLY moved past the second reading, so one of
 * these arriving for it can only be an out-of-order arrival, never news.
 *
 * The case this exists for is the hydrate race. `hydrateStream()` is fired un-awaited while the live
 * subscription attaches synchronously (DocumentCapturePage), so a replayed snapshot can be applied
 * AFTER a live event for the same file — a `held` from thirty seconds ago landing on top of a live
 * `captured`. The row then parked on `held` FOREVER, because `held` is deliberately absent from
 * {@link RETIRABLE_PHASES} and so is never swept away: the operator read "waiting for the second
 * reader" on a scan that was captured and filed.
 *
 * ⚠️ DO NOT replace this with a blanket keep-higher-rank guard. Legitimate backwards server
 * transitions exist, and they are not exotic — they are on the happy path of every single document:
 *
 *   • `captured` (5) → `waiting` (3). DocScanLinkHandler publishes `waiting` at DOCUMENT level (null
 *     fileId + docNumber), so it lands on the same row the scan-level `captured` created. A blanket
 *     guard would delete the "waiting to link" step from the feed for every scan.
 *   • `captured` (5) → `review` (4). The same handler publishes `review` this way for a NetSuite
 *     ambiguity or a voided transaction. A blanket guard would leave a document that NEEDS A PERSON
 *     sitting on screen as "captured" — and `captured` IS retirable, so it would then step aside and
 *     take the exception with it. That is the worst outcome available here.
 *
 * The replace flow (W9) is a NEAR MISS worth recording, because it looks like a third case and is not:
 * DocumentFileUpdateService publishes `reading` WITH a docNumber for an already-`linked` document, and
 * LiveStreamPanel keys its "Updating — reading the file…" copy off exactly that — but the event carries
 * the NEW file id, and the docNumber fallback in the matcher only applies to events with no file id at
 * all. So a replace never morphs the linked row: it starts its own. (Which means the feed shows two
 * rows for one document mid-replace. Deliberate or not, it is what ships, and it is pinned by test.)
 */
export const STALLED_STEP_PHASES: ReadonlySet<DocStreamPhase> = new Set<DocStreamPhase>([
  'held',
  'retrying',
]);

/**
 * 86d3z2nyz — terminal verdicts about ONE FILE that is not, and will never be, the version the
 * document serves. A DOCUMENT-level event must never be allowed to re-label one of these.
 *
 * The case (owner decision 2026-08-07, "two honest rows"): the same paper scanned twice before the
 * link lands leaves two rows for one document, and the link lane publishes exactly ONE `linked` —
 * document-level, with a null file id. That event is matched by document number and takes the FIRST
 * row carrying it, so whichever of the two happens to sit higher consumes it. The server now marks
 * the displaced version `duplicate` (DocumentAttachService), and this set is the client-side half of
 * the same promise: once a row has stated its own file's terminal answer, a later document-level
 * event cannot overwrite it with "Linked".
 *
 * It also closes a phantom the tally has always had, independent of any of the above.
 * {@link tallyFromRows} counts a `linked` row as BOTH linked and captured, so re-labelling a
 * `duplicate` row — the client's own terminal answer for a re-scan, set from the upload response,
 * with a document number attached — reported a capture and a link that never happened. Same for a
 * `failed` replace upload, which DocScanReplaceHandler publishes WITH the document number.
 *
 * ⚠️ Membership rule, and the only one: a phase belongs here if it is (a) terminal for that file and
 * (b) never the serving version. `captured`, `waiting`, `review` and `held` are none of those things
 * — they describe a file still on its way somewhere, and the doc-level `waiting`/`review`/`linked`
 * events MUST keep landing on them. Adding one of those here would delete the link lane's steps from
 * the feed for every document, which is the failure {@link STALLED_STEP_PHASES} documents at length.
 *
 * Scoped to the DOCUMENT-NUMBER fallback only. An event naming this exact file id (or its correlation
 * id) is news about this file and still applies — that is how the server re-labels the row in the
 * first place.
 */
export const FILE_TERMINAL_PHASES: ReadonlySet<DocStreamPhase> = new Set<DocStreamPhase>([
  'duplicate',
  'failed',
]);

/**
 * Apply one SSE event to the stream, returning the new stream (newest-first, capped).
 *
 * Match priority: (1) the client correlation id — the ONLY identifier that exists before the upload
 * response, so this reconciles even when that response was lost; (2) the server file id; (3) the doc
 * number for document-level events (link lane, T7 'updated'), skipping rows that already carry a
 * terminal answer about their own file (see {@link FILE_TERMINAL_PHASES}).
 *
 * ⚠️ Server events are emitted in order but do NOT necessarily ARRIVE in order — this docstring used
 * to say a downgrade guard "is not needed here" on exactly that reasoning, and the hydrate replay
 * breaks it (see {@link STALLED_STEP_PHASES}). So there is now ONE guard, and it is narrow on purpose:
 * a stalled-step event that would overwrite a further-along row is dropped whole. Everything else is
 * applied exactly as before — above all the doc-level `waiting` and `review` events, which walk a
 * `captured` row backwards on the happy path and MUST keep doing so.
 *
 * Known and accepted residue: the same race can still replay a `reading`/`verifying` on top of a live
 * `captured`, and those two must stay applicable (the replace flow is indistinguishable from the
 * outside). Distinguishing replay from live needs a signal the reducer does not have — `at` is not
 * usable, since locally-created rows are stamped from the CLIENT clock and skew would start dropping
 * real events. The fix for that one belongs at the hydrate/subscribe seam, not here.
 *
 * 86d3yeh5j — a phase CHANGE un-retires the row (see {@link carryRetirement}). Without that,
 * the doc-level match above guarantees the bug: the NetSuite link lane lags, so its `linked` event
 * (null fileId, docNumber set) routinely arrives after a `captured` row has already retired, and it
 * would morph that row WHILE HIDDEN — the "Linked ✓" the operator is waiting for never appears. A
 * `review`/`failed` morph is worse still: it would file a row that needs a person under a footer
 * labelled "finished and filed".
 */
export function reduceStreamEvent(stream: JourneyRow[], event: DocStreamEvent): JourneyRow[] {
  const index = stream.findIndex(
    (row) =>
      (event.clientUploadId != null && row.clientUploadId === event.clientUploadId) ||
      (event.fileId != null && row.fileId === event.fileId) ||
      // 86d3z2nyz — the document-number fallback SKIPS rows that have stated a terminal answer about
      // their own file (see FILE_TERMINAL_PHASES). Without it a document-level `linked` lands on a
      // duplicate or a failed upload and calls it filed, and the tally gains a capture and a link
      // that never happened. If no other row carries the number, the event falls through to the
      // prepend below and the document gets its own row — two honest rows, which is the decision.
      (event.fileId == null &&
        event.docNumber != null &&
        row.docNumber === event.docNumber &&
        !FILE_TERMINAL_PHASES.has(row.phase))
  );

  if (index >= 0) {
    // The one downgrade guard on the server path (see STALLED_STEP_PHASES for why only these two).
    // `waiting` is above them in rank and therefore also protected, which is right: the second
    // reading happens BEFORE a document queues for the NetSuite link, so a hold reported for a row
    // already waiting to link is as stale as one reported for a captured row.
    //
    // The event is dropped WHOLE — the same array reference back — rather than merged with its phase
    // ignored. A stale `held` carries a stale `at` and a `holdReason`, and stamping either onto a
    // captured row would misdate it and flavour it with a hold it is not in. Returning the same
    // reference also costs the subscribers nothing: the store's `set` publishes a new state object
    // but `stream` compares equal, so no selector fires (CLAUDE.md re-render gate).
    if (
      STALLED_STEP_PHASES.has(event.phase) &&
      PHASE_RANK[stream[index].phase] > PHASE_RANK[event.phase]
    )
      return stream;

    const next = [...stream];
    next[index] = {
      ...next[index],
      phase: event.phase,
      docNumber: event.docNumber ?? next[index].docNumber,
      prefix: event.prefix ?? next[index].prefix,
      fileId: event.fileId ?? next[index].fileId,
      clientUploadId: event.clientUploadId ?? next[index].clientUploadId,
      at: event.at,
      // 86d3yeh4j — NOT `?? previous`. The try counter belongs to the retry the event describes, so
      // any later phase must drop it; carrying it forward would leave a captured row still claiming
      // to be on try 2 of 4.
      attempt: event.attempt ?? undefined,
      of: event.of ?? undefined,
      holdReason: event.holdReason ?? undefined,
      ...carryRetirement(next[index], event.phase),
    };
    return next;
  }

  const row: JourneyRow = {
    key: event.clientUploadId ?? (event.fileId != null ? `f${event.fileId}` : `d${event.docNumber}`),
    fileId: event.fileId,
    docNumber: event.docNumber,
    prefix: event.prefix,
    phase: event.phase,
    at: event.at,
    clientUploadId: event.clientUploadId ?? undefined,
    attempt: event.attempt ?? undefined,
    of: event.of ?? undefined,
    holdReason: event.holdReason ?? undefined,
  };
  return [row, ...stream].slice(0, STREAM_CAP);
}

/**
 * Upsert a local optimistic row for a file THIS browser is uploading. A watcher RETRY of the same
 * file reuses its stable key; if the row already exists — possibly already advanced by SSE (server
 * succeeded, response lost) — leave it untouched rather than prepend a duplicate or downgrade it.
 */
export function addLocalRow(
  stream: JourneyRow[],
  key: string,
  fileName: string,
  clientUploadId?: string,
  now: string = new Date().toISOString()
): JourneyRow[] {
  if (stream.some((r) => r.key === key)) return stream;
  const row: JourneyRow = {
    key,
    fileId: null,
    docNumber: null,
    prefix: null,
    phase: 'uploading',
    fileName,
    at: now,
    clientUploadId: clientUploadId ?? key,
  };
  return [row, ...stream].slice(0, STREAM_CAP);
}

/**
 * Settle a row a PERSON just identified in the review queue (86d3x48q7 T13, bundle §4.5).
 *
 * The row is moved to `waiting` — "you confirmed it, now writing the link into NetSuite" — and the
 * server's own `linked` event completes it. It must NOT jump straight to `linked`: that is not what
 * happened, and the feed is where operators learn the pipeline's shape.
 *
 * ⚠️ This DELIBERATELY BYPASSES the keep-higher-rank guard, and that is the whole reason it is a
 * separate function rather than a call to {@link resolveLocalRow}. The row is sitting at `review`
 * (rank 4) and is moving to `waiting` (rank 3), which the guard would reject as a downgrade — but
 * the guard exists to stop LATE ASYNC EVENTS from clobbering a row that has moved on, and this is
 * neither late nor async: it is the user's own decision about this exact row, taken now. Ranking
 * cannot express "backwards in the pipeline, forwards in reality", so the caller does.
 *
 * Matched by fileId first, then docNumber — a review item may carry either, and rows for scans this
 * browser never uploaded have no correlation id. **No row is invented when none matches**: a
 * reviewer clearing yesterday's queue is not watching those scans arrive, and a phantom row for a
 * file nobody saw upload would be noise, not feedback.
 */
export function settleResolvedRow(
  stream: JourneyRow[],
  match: { fileId: number | null; docNumber: string | null },
  now: string = new Date().toISOString()
): JourneyRow[] {
  const index = stream.findIndex(
    (row) =>
      (match.fileId != null && row.fileId === match.fileId) ||
      (match.docNumber != null && row.docNumber === match.docNumber)
  );
  if (index < 0) return stream;

  const next = [...stream];
  next[index] = {
    ...next[index],
    phase: 'waiting',
    docNumber: match.docNumber ?? next[index].docNumber,
    resolvedByYou: true,
    at: now,
    // 86d3yeh5j — same invariant as reduceStreamEvent. A reviewer can settle a document whose row
    // already retired (a `linked` row re-opened, a duplicate resolved); "you confirmed it — writing
    // the link into NetSuite" is feedback for an action the user just took, so it cannot land inside
    // a collapsed footer.
    ...carryRetirement(next[index], 'waiting'),
  };
  return next;
}

/**
 * Resolve a local row after the upload responds. Never DOWNGRADES a row the stream already advanced
 * (a late retry-'duplicate' or give-up-'failed' must not clobber a real captured/linked), and
 * collapses a separate row an out-of-order SSE event may have created for the same file id.
 */
export function resolveLocalRow(
  stream: JourneyRow[],
  key: string,
  phase: DocStreamPhase,
  detail?: Partial<Pick<JourneyRow, 'docNumber' | 'prefix' | 'fileId' | 'duplicateStatus'>>,
  now: string = new Date().toISOString()
): JourneyRow[] {
  const index = stream.findIndex((row) => row.key === key);
  if (index < 0) return stream;
  const current = stream[index];
  let next = [...stream];
  const keepPhase = PHASE_RANK[phase] < PHASE_RANK[current.phase] ? current.phase : phase;
  const merged: JourneyRow = { ...current, ...detail, phase: keepPhase, at: now };

  if (merged.fileId != null) {
    const dupIndex = next.findIndex((row, i) => i !== index && row.fileId === merged.fileId);
    if (dupIndex >= 0) {
      const dup = next[dupIndex];
      if (PHASE_RANK[dup.phase] > PHASE_RANK[merged.phase]) merged.phase = dup.phase;
      merged.docNumber = merged.docNumber ?? dup.docNumber;
      merged.prefix = merged.prefix ?? dup.prefix;
    }
    next[index] = merged;
    next = next.filter((row, i) => i === index || row.fileId !== merged.fileId);
  } else {
    next[index] = merged;
  }
  // 86d3yeh5j — the same un-retire invariant, decided LAST because the collapse above can bump the
  // phase a second time, and keyed off the phase actually KEPT rather than the one requested: the
  // rank guard turns most late callbacks into no-ops, and a no-op must not put a row back on screen.
  Object.assign(merged, carryRetirement(current, merged.phase));
  return next;
}
